import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Headphones,
  FileText,
  Play,
  Pause,
  Moon,
  Sparkles,
  CheckCircle2,
  Lock,
  Loader2,
  Printer,
  ListChecks,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, '0');
  return `${m}:${ss}`;
};

function AudioCollection({ collection }) {
  const [activeTrackId, setActiveTrackId] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const hasTracks = collection.tracks && collection.tracks.length > 0;
  const activeTrack = hasTracks ? collection.tracks.find((t) => t.id === activeTrackId) : null;

  const playTrack = useCallback((track) => {
    if (!track?.audio_url) {
      toast.info('Audio file coming soon — Breah is finalizing the recording.');
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.src !== track.audio_url) {
      audio.src = track.audio_url;
    }
    audio.currentTime = track.start_seconds || 0;
    audio.play().then(() => {
      setActiveTrackId(track.id);
      setPlaying(true);
    }).catch(() => toast.error('Could not start playback.'));
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().then(() => setPlaying(true));
    else { audio.pause(); setPlaying(false); }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const onTime = () => {
      setCurrentTime(audio.currentTime);
      // If track has end_seconds, stop at the boundary (for chaptered files)
      if (activeTrack?.end_seconds && audio.currentTime >= activeTrack.end_seconds) {
        audio.pause();
        setPlaying(false);
      }
    };
    const onDuration = () => setDuration(audio.duration);
    const onEnded = () => setPlaying(false);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onDuration);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onDuration);
      audio.removeEventListener('ended', onEnded);
    };
  }, [activeTrack]);

  return (
    <Card className="bg-card/60 border-silver/15 p-6" data-testid={`audio-collection-${collection.id}`}>
      <div className="flex items-start gap-3 mb-5">
        <Headphones className="w-5 h-5 mt-1 text-secondary shrink-0" />
        <div>
          <h3 className="font-heading text-xl text-foreground">{collection.title}</h3>
          <p className="text-sm text-muted-foreground mt-1">{collection.subtitle}</p>
        </div>
      </div>

      {!hasTracks ? (
        <p className="text-sm text-muted-foreground italic" data-testid={`audio-collection-empty-${collection.id}`}>
          Tracks coming soon — Breah is finalizing the recordings.
        </p>
      ) : (
        <ul className="space-y-2">
          {collection.tracks.map((track, idx) => {
            const isActive = activeTrackId === track.id;
            const isPending = !track.audio_url;
            return (
              <li key={track.id}>
                <button
                  onClick={() => (isActive ? togglePlay() : playTrack(track))}
                  disabled={isPending}
                  className={`w-full flex items-center gap-3 text-left rounded-lg px-3 py-2.5 transition-colors ${
                    isActive ? 'bg-secondary/15 border border-secondary/40' :
                    isPending ? 'bg-card/20 border border-silver/10 opacity-60 cursor-not-allowed' :
                    'bg-card/40 border border-silver/15 hover:bg-card/70'
                  }`}
                  data-testid={`audio-track-${track.id}`}
                >
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${
                    isActive ? 'bg-secondary text-secondary-foreground' : 'bg-card text-muted-foreground border border-silver/30'
                  }`}>
                    {isPending ? <Lock className="w-3 h-3" /> : isActive && playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3 ml-0.5" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${isActive ? 'text-secondary' : 'text-foreground'}`}>
                      {idx + 1}. {track.title}
                    </p>
                    {track.duration_label && (
                      <p className="text-[11px] text-muted-foreground">{track.duration_label}</p>
                    )}
                  </div>
                  {isActive && !isPending && (
                    <span className="text-[11px] text-secondary font-mono">{fmtTime(currentTime - (track.start_seconds || 0))} / {track.duration_label || fmtTime(duration)}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <audio ref={audioRef} preload="none" />
    </Card>
  );
}

function CheckinForm({ token, prompts }) {
  const [created, setCreated] = useState('');
  const [noticed, setNoticed] = useState('');
  const [lettingGo, setLettingGo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const submit = async () => {
    if (!created.trim() && !noticed.trim() && !lettingGo.trim()) {
      toast.info('Write something in at least one box first 💛');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/practice/checkin`, {
        token,
        created_response: created,
        noticed_response: noticed,
        letting_go_response: lettingGo,
      });
      setSavedAt(new Date());
      setCreated(''); setNoticed(''); setLettingGo('');
      toast.success('Check-in saved. Rest well. 🌙');
    } catch {
      toast.error('Could not save check-in. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const promptOf = (i) => prompts?.[i] || ['What did I create today?', 'What did I noticed?', 'What am I letting go of?'][i];

  return (
    <Card className="bg-card/60 border-silver/15 p-6" data-testid="practice-checkin-form">
      <div className="flex items-start gap-3 mb-5">
        <Moon className="w-5 h-5 mt-1 text-secondary shrink-0" />
        <div>
          <h3 className="font-heading text-xl text-foreground">Evening Check-In</h3>
          <p className="text-sm text-muted-foreground mt-1">Close the day. Three questions. Three minutes.</p>
        </div>
      </div>
      <div className="space-y-4">
        {[
          { label: promptOf(0), val: created, set: setCreated, key: 'created' },
          { label: promptOf(1), val: noticed, set: setNoticed, key: 'noticed' },
          { label: promptOf(2), val: lettingGo, set: setLettingGo, key: 'letting-go' },
        ].map((q) => (
          <div key={q.key}>
            <label className="text-sm font-medium text-foreground/90 block mb-1.5">{q.label}</label>
            <Textarea
              value={q.val}
              onChange={(e) => q.set(e.target.value)}
              placeholder="Just a sentence or two…"
              className="bg-card/40 border-silver/20 min-h-[72px] text-sm"
              data-testid={`checkin-${q.key}`}
            />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-5">
        <p className="text-xs text-muted-foreground">
          {savedAt ? `Saved ${savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ✓` : 'Private — only you can see your check-ins.'}
        </p>
        <Button
          onClick={submit}
          disabled={submitting}
          className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
          data-testid="checkin-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save check-in'}
        </Button>
      </div>
    </Card>
  );
}

export default function PracticePortalPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(() => Boolean(params.get('token')));
  const [error, setError] = useState(() => (params.get('token') ? '' : 'Missing access token.'));

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const id = setTimeout(() => {
      axios.get(`${API}/practice/portal-access`, { params: { token } })
        .then((r) => { if (!cancelled) setData(r.data); })
        .catch((e) => {
          if (!cancelled) setError(e?.response?.data?.detail || 'Invalid or expired link. Request a new one from the landing page.');
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 0);
    return () => { cancelled = true; clearTimeout(id); };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="practice-portal-loading">
        <Loader2 className="w-6 h-6 animate-spin text-secondary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="practice-portal-error">
        <Card className="max-w-md w-full p-7 text-center bg-card border-silver/20">
          <Lock className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <h2 className="font-heading text-xl text-foreground mb-2">Access required</h2>
          <p className="text-sm text-muted-foreground mb-5">{error || 'This link is no longer valid.'}</p>
          <Link to="/practice">
            <Button className="bg-secondary text-secondary-foreground hover:bg-secondary/90">Get access</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const { access, library, checkin_prompts } = data;

  return (
    <div className="min-h-screen bg-background" data-testid="practice-portal-root">
      {/* HEADER */}
      <section className="border-b border-silver/10 bg-card/30">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-secondary font-semibold mb-1">Power Planning Practice</p>
              <h1 className="font-heading text-3xl md:text-4xl text-foreground">Welcome home, friend.</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Signed in as <span className="text-foreground/80 font-medium">{access.email}</span>
                {access.granted_via === 'ebook-bonus' && <Badge variant="outline" className="ml-2 text-xs border-secondary/30 text-secondary">🎁 E-Book Bonus</Badge>}
                {access.granted_via === 'collective-bonus' && <Badge variant="outline" className="ml-2 text-xs border-secondary/30 text-secondary">👑 Collective Member</Badge>}
                {access.granted_via === 'standalone' && <Badge variant="outline" className="ml-2 text-xs border-secondary/30 text-secondary">✨ Lifetime Access</Badge>}
              </p>
            </div>
            <Badge variant="outline" className="border-secondary/30 text-secondary">
              <Sparkles className="w-3 h-3 mr-1 inline" /> {library.audio_collections.length} meditation sets · {library.worksheets.length} worksheets
            </Badge>
          </div>
        </div>
      </section>

      {/* LIBRARY GRID */}
      <section>
        <div className="container mx-auto px-4 py-10 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-xs uppercase tracking-widest text-secondary font-semibold mb-2">Meditations</p>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground mb-6">Pick your practice for right now.</h2>
            <div className="grid lg:grid-cols-3 gap-5 mb-12">
              {library.audio_collections.map((c) => (
                <AudioCollection key={c.id} collection={c} />
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-xs uppercase tracking-widest text-secondary font-semibold mb-2">Worksheets</p>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground mb-6">Print, fill, integrate.</h2>
            <div className="grid md:grid-cols-3 gap-5 mb-12">
              {library.worksheets.map((w) => (
                <Card key={w.id} className="bg-card/60 border-silver/15 p-5" data-testid={`worksheet-${w.id}`}>
                  <FileText className="w-5 h-5 text-secondary mb-3" />
                  <h3 className="font-heading text-lg text-foreground mb-1.5">{w.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-4">{w.subtitle}</p>
                  <a href={w.route} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" className="w-full border-secondary/40 text-secondary hover:bg-secondary/10" data-testid={`worksheet-open-${w.id}`}>
                      <Printer className="w-3.5 h-3.5 mr-2" /> Open / Print
                    </Button>
                  </a>
                </Card>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-xs uppercase tracking-widest text-secondary font-semibold mb-2 flex items-center gap-2"><ListChecks className="w-3.5 h-3.5" /> Close the day</p>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground mb-6">Evening Check-In.</h2>
            <CheckinForm token={token} prompts={checkin_prompts} />
          </motion.div>

          <div className="mt-10 text-center" data-testid="practice-portal-footer">
            <p className="text-xs text-muted-foreground">
              Bookmark this page — your access never expires.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
